import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'preact/hooks'
import type { Shift } from '../api/generated/Shift'
import type { ShiftSplit } from '../api/generated/ShiftSplit'
import { useAction } from '../api/useAction'
import { type Slicing, SlicingField, toSplit } from './SlicingField'
import { sliceBounds } from './slices'
import { formatDuration, formatRange, minutesBetween } from './time'

const MAX_PARTS = 48

/** `4 créneaux de 2 h`, or `3 créneaux : 2 h, 2 h, 1 h` when they differ. */
function describe(bounds: string[]): string {
  const durations = bounds.slice(1).map((end, i) => minutesBetween(bounds[i] ?? end, end))
  const count = `${durations.length} créneaux`
  const [first] = durations
  return first !== undefined && durations.every((d) => d === first)
    ? `${count} de ${formatDuration(first)}`
    : `${count} : ${durations.map(formatDuration).join(', ')}`
}

export function SplitDialog({
  shift,
  onSubmit,
  onClose,
}: {
  shift: Shift
  onSubmit: (split: ShiftSplit) => Promise<void>
  onClose: () => void
}) {
  const [slicing, setSlicing] = useState<Slicing>({ by: 'parts', amount: '2' })
  const { error, pending, run } = useAction()
  const split = toSplit(slicing)
  const bounds = sliceBounds(shift.starts_at, shift.ends_at, split)
  const parts = bounds.length - 1
  const valid = parts >= 2 && parts <= MAX_PARTS
  const submit = (e: Event) => {
    e.preventDefault()
    run(() => onSubmit(split))
  }
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>Découper le créneau</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              {formatRange(shift.starts_at, shift.ends_at)}, {shift.required_volunteers} bénévole
              {shift.required_volunteers > 1 ? 's' : ''} par créneau.
            </DialogContentText>
            <SlicingField value={slicing} onChange={setSlicing} />
            {valid && <Typography variant="body2">{describe(bounds)}</Typography>}
            {parts === 1 && (
              <Alert severity="info">
                Un seul créneau : choisissez une durée plus courte ou un nombre plus grand.
              </Alert>
            )}
            {parts > MAX_PARTS && <Alert severity="warning">{MAX_PARTS} créneaux au plus.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button type="submit" variant="contained" loading={pending} disabled={!valid}>
            Découper
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
